/*
 * (c) copyright 2021 International Road Dynamics Inc. and related companies.
 * All rights reserved.
 * Copyright as an unpublished work.
 *
 * This file contains confidential information of International
 * Road Dynamics Inc. and related companies, and should be treated as proprietary
 * and confidential.
 *
 * The use, copying, disclosure or modification of the
 * programs and/or source code is prohibited unless
 * otherwise provided for in the license or confidentiality
 * agreements.
 *
 * Any copy must contain this copyright notice.
 */

import React from 'react'
import PropTypes from 'prop-types'

import {
  GoogleMap,
  LoadScript
} from '@react-google-maps/api'

import MapLegend from '../../components/MapLegend/MapLegend'
import MapMarker from '../../components/MapMarker/MapMarker'

import './MapContainer.css'

class MapContainer extends React.Component {
  constructor (props) {
    super(props)

    this.defaultCenter = {
      lat: 21.338563490598283,
      lng: -157.8596131300009
    }

    this.state = {
      mapInstance: null,
      selectedSiteId: null
    }

    this.handleMarkerClose = this.handleMarkerClose.bind(this)
    this.handleMarkerClick = this.handleMarkerClick.bind(this)
    this.handleOnMapLoad = this.handleOnMapLoad.bind(this)
    this.handleOnMapUnmount = this.handleOnMapUnmount.bind(this)
  }

  componentDidUpdate (prevProps) {
    // Fit all sites into view when map instance is present, and there are changes to list of sites
    if (this.state.mapInstance && prevProps.sites !== this.props.sites) {
      this.fitBounds(this.state.mapInstance)
    }

    // Clear selected site ID when it doesn't exist
    if (this.state.selectedSiteId !== null && !this.props.sites.find((site) => site.id === this.state.selectedSiteId)) {
      this.setState({ selectedSiteId: null })
    }
  }

  /**
   * Function to adjust map zoom/location to fit all sites in view
   *
   * @param {Object} mapInstance Google Map instance
   */
  fitBounds (mapInstance) {
    // Only fit bounds when there is at least 1 site
    if (this.props.sites.length > 0) {
      const bounds = new window.google.maps.LatLngBounds()

      this.props.sites.forEach((site) => {
        const latLong = new window.google.maps.LatLng(site.latitude, site.longitude)
        bounds.extend(latLong)
      })

      mapInstance.fitBounds(bounds)

      // If only 1 site available, set zoom level to 13 to prevent zooming in too much
      if (this.props.sites.length === 1) {
        mapInstance.setZoom(13)
      }
    }
  }

  handleMarkerClose () {
    this.setState({ selectedSiteId: null })
  }

  handleMarkerClick (siteId) {
    this.setState({ selectedSiteId: siteId })
  }

  handleOnMapLoad (mapInstance) {
    this.fitBounds(mapInstance)

    if (this.state.mapInstance === null) {
      this.setState({ mapInstance })
    }
  }

  handleOnMapUnmount () {
    this.setState({ mapInstance: null })
  }

  render () {
    const markers = this.props.sites.map((site) => {
      return (
        <MapMarker
          key={site.id}
          onClick={this.handleMarkerClick}
          onClose={this.handleMarkerClose}
          isSelected={site.id === this.state.selectedSiteId}
          site={site}
        />
      )
    })

    return (
      <div>
        <LoadScript
          googleMapsApiKey={process.env.REACT_APP_GOOGLE_API_KEY}
        >
          <GoogleMap
            id="hawaiiMap"
            center={this.defaultCenter}
            zoom={7}
            onLoad={this.handleOnMapLoad}
            onUnmount={this.handleOnMapUnmount}
          >
            {markers}
            <MapLegend />
          </GoogleMap>
        </LoadScript>
      </div>
    )
  }
}

MapContainer.propTypes = {
  sites: PropTypes.array
}

MapContainer.defaultProps = {
  sites: []
}

export default MapContainer
